// Bring-your-own-key store for the cloud mentor. The key lives ONLY in the
// browser — sessionStorage by default (gone when the tab closes), or
// localStorage if the user ticks "remember on this device". It is never sent
// to our backend, Firestore, or any *.run.app / firebase endpoint — only to
// the chosen provider's own HTTPS API (see cloudProviders.js).

const STORAGE_KEY = "byok.v1";

// Read from sessionStorage first (the default, more private), then
// localStorage (the opt-in "remember" slot). Returns `{ provider, key, model }`
// (`model` undefined = provider default) or
// null. Never throws — private-mode / disabled storage just yields null.
export function loadByok() {
  for (const store of [safeSession(), safeLocal()]) {
    try {
      const raw = store && store.getItem(STORAGE_KEY);
      if (!raw) continue;
      const v = JSON.parse(raw);
      if (v && typeof v.key === "string" && v.key && (v.provider === "gemini" || v.provider === "openai")) {
        return { provider: v.provider, key: v.key, model: typeof v.model === "string" && v.model ? v.model : undefined };
      }
    } catch (e) {
      /* fall through to the next store */
    }
  }
  return null;
}

// Persist the key. `remember: true` -> localStorage (survives restarts),
// otherwise sessionStorage. Writing one slot clears the other so there is
// only ever one copy.
export function saveByok({ provider, key, model, remember = false }) {
  const payload = JSON.stringify({ provider, key, ...(model ? { model } : {}) });
  try {
    if (remember) {
      safeLocal() && safeLocal().setItem(STORAGE_KEY, payload);
      safeSession() && safeSession().removeItem(STORAGE_KEY);
    } else {
      safeSession() && safeSession().setItem(STORAGE_KEY, payload);
      safeLocal() && safeLocal().removeItem(STORAGE_KEY);
    }
  } catch (e) {
    /* storage unavailable — the key just won't persist */
  }
}

export function clearByok() {
  try { safeSession() && safeSession().removeItem(STORAGE_KEY); } catch (e) {}
  try { safeLocal() && safeLocal().removeItem(STORAGE_KEY); } catch (e) {}
}

// `true` when the key is currently in the durable (localStorage) slot.
export function byokIsRemembered() {
  try { return !!(safeLocal() && safeLocal().getItem(STORAGE_KEY)); } catch (e) { return false; }
}

// Display form: first 4 + last 4, middle elided. Never render the raw key.
export function maskKey(k = "") {
  if (k.length <= 10) return "••••••";
  return `${k.slice(0, 4)}…${k.slice(-4)}`;
}

// Cheap shape check so we can disable "Salva" before a doomed request. Not a
// validity guarantee — the provider's first 401 is the real check.
export function looksLikeKey(provider, k = "") {
  const key = k.trim();
  if (provider === "openai") return /^sk-[A-Za-z0-9_-]{20,}$/.test(key);
  if (provider === "gemini") return /^AIza[A-Za-z0-9_-]{20,}$/.test(key);
  return key.length >= 20;
}

function safeSession() {
  try { return window.sessionStorage; } catch (e) { return null; }
}
function safeLocal() {
  try { return window.localStorage; } catch (e) { return null; }
}
